import { z } from 'zod';
import { REGISTRAR_KINDS, type RegistrarKind } from './domain';

// The reasons of the delivered integration guide v1.1 (§4.1.4) and data-model
// specification v1.1 (core.stock_movement_reason). Every code is a reason of its own
// on every channel: NC-TERM-04 (modified 2026-10-12) and CF-15 dropped the combined
// "comercializare/eliberare", and the reason table gives a pharmacy both SALE and
// DISPENSING.
export const stockMovementReasonSchema = z.enum([
  'IMPORT',
  'PURCHASE',
  'DONATION',
  'EXPORT',
  'SALE',
  'DISPENSING',
  'TRANSFER',
  'DESTRUCTION',
  'CORRECTION',
]);
export type StockMovementReason = z.infer<typeof stockMovementReasonSchema>;

export const submissionStatusSchema = z.enum(['DRAFT', 'CONFIRMED']);
export type SubmissionStatus = z.infer<typeof submissionStatusSchema>;

export const submissionSourceSchema = z.enum(['AUTOMATED', 'MANUAL']);
export type SubmissionSource = z.infer<typeof submissionSourceSchema>;

/** The reasons that raise a lot's stock, and those that lower it (REASON_DIRECTION below). */
export const INFLOW_REASONS: ReadonlyArray<StockMovementReason> = [
  'IMPORT',
  'PURCHASE',
  'DONATION',
];
export const OUTFLOW_REASONS: ReadonlyArray<StockMovementReason> = [
  'EXPORT',
  'SALE',
  'DISPENSING',
  'TRANSFER',
  'DESTRUCTION',
];
export const COMMENT_REQUIRED_REASONS: ReadonlyArray<StockMovementReason> = [
  'TRANSFER',
  'DESTRUCTION',
];

/** Which sign of `currentQuantity − previousQuantity` a reason may carry. */
export type ReasonDirection = 'increase' | 'decrease' | 'either';

/** The one direction map (specs/054 research.md R3). */
export const REASON_DIRECTION: Readonly<Record<StockMovementReason, ReasonDirection>> = {
  IMPORT: 'increase',
  PURCHASE: 'increase',
  DONATION: 'increase',
  EXPORT: 'decrease',
  SALE: 'decrease',
  DISPENSING: 'decrease',
  TRANSFER: 'decrease',
  DESTRUCTION: 'decrease',
  CORRECTION: 'either',
};

/** The reasons one Registrar type may report, inflows and outflows apart. */
export interface RegistrarKindReasons {
  readonly increase: ReadonlyArray<StockMovementReason>;
  readonly decrease: ReadonlyArray<StockMovementReason>;
}

/**
 * The reasons each Registrar type may report (UC50-15), verbatim from the CF-15
 * decision of 2026-10-12, in its order. CORRECTION is in no row: it stays available
 * to every type (UC50 step 8), and `reasonAllowedFor` says so. The manual screen, the
 * draft, the system API and the import all read this one table (TC-UC50-15-05).
 */
export const REASONS_BY_REGISTRAR_KIND: Readonly<Record<RegistrarKind, RegistrarKindReasons>> = {
  MANUFACTURER: {
    increase: ['IMPORT', 'DONATION'],
    decrease: ['SALE', 'EXPORT', 'TRANSFER', 'DESTRUCTION'],
  },
  IMPORTER: {
    increase: ['IMPORT', 'DONATION'],
    decrease: ['SALE', 'EXPORT', 'TRANSFER', 'DESTRUCTION'],
  },
  WAREHOUSE: {
    increase: ['PURCHASE', 'DONATION'],
    decrease: ['SALE', 'TRANSFER', 'EXPORT', 'DESTRUCTION'],
  },
  PHARMACY: {
    increase: ['PURCHASE'],
    decrease: ['DISPENSING', 'SALE', 'TRANSFER', 'DESTRUCTION'],
  },
  IMSP: {
    increase: ['PURCHASE', 'DONATION'],
    decrease: ['DISPENSING', 'TRANSFER', 'DESTRUCTION'],
  },
  PRIVATE_MEDICAL: {
    increase: ['PURCHASE', 'DONATION'],
    decrease: ['DISPENSING', 'SALE', 'TRANSFER', 'DESTRUCTION'],
  },
};

/**
 * The type a kind code names, or null for none. Catalog's column holds one of the
 * six for every Registrar, but `core.dim_registrar.kind` is a varchar and the wire
 * carries a string, so a peer on another contract version can send a value outside
 * them; it names no type.
 */
export function knownRegistrarKind(kind: string | null | undefined): RegistrarKind | null {
  return (REGISTRAR_KINDS as ReadonlyArray<string>).includes(kind ?? '')
    ? (kind as RegistrarKind)
    : null;
}

/**
 * The reasons a Registrar of this type may report, in the table's order: its
 * inflows, its outflows, then CORRECTION. UC50-15 allows only the reasons of the
 * Registrar's type, so a value naming no type allows none
 * (docs/shape-ops-registrar-kind-required.md §Decision 4).
 */
export function reasonsAllowedFor(
  kind: string | null | undefined,
): ReadonlyArray<StockMovementReason> {
  const known = knownRegistrarKind(kind);
  if (!known) return [];
  const { increase, decrease } = REASONS_BY_REGISTRAR_KIND[known];
  return [...increase, ...decrease, 'CORRECTION'];
}

/** Whether a Registrar of this type may report this reason (UC50-15, CF-15). */
export function reasonAllowedFor(
  reason: StockMovementReason,
  kind: string | null | undefined,
): boolean {
  return reasonsAllowedFor(kind).includes(reason);
}

// Caiet UC50 step 2: the reasons "asociate tipului Registratorului" ("associated
// with the Registrar's type"). A medical institution dispenses; everyone else sells.
const DISPENSING_REGISTRAR_KINDS: ReadonlySet<RegistrarKind> = new Set(['IMSP', 'PRIVATE_MEDICAL']);

/**
 * The reason a row of this direction starts from while the draft remembers none
 * (specs/054 research.md R4), for the Registrar's type. An increase starts from the
 * type's first inflow (IMPORT for a manufacturer or an importer, PURCHASE for the
 * rest); a decrease from DISPENSING at a medical institution and SALE elsewhere,
 * which is what the "Comercializare/Eliberare" grouping wrote for a default. Each
 * is a reason its type allows.
 */
export function startingReasonFor(
  direction: Exclude<ReasonDirection, 'either'>,
  registrarKind: RegistrarKind,
): StockMovementReason {
  if (direction === 'increase') {
    // Every row of the table names at least one inflow.
    return REASONS_BY_REGISTRAR_KIND[registrarKind].increase[0] ?? 'PURCHASE';
  }
  return DISPENSING_REGISTRAR_KINDS.has(registrarKind) ? 'DISPENSING' : 'SALE';
}

/**
 * Whether a reason may carry a difference of this sign. A difference of 0 fits
 * no direction; it is refused on its own (QUANTITY_UNCHANGED), not as a reason.
 */
export function reasonFitsDifference(reason: StockMovementReason, difference: number): boolean {
  const direction = REASON_DIRECTION[reason];
  if (direction === 'either') return difference !== 0;
  return direction === 'increase' ? difference > 0 : difference < 0;
}

/** The two directions a movement can have, the keys of the default reasons. */
export const movementDirectionSchema = z.enum(['increase', 'decrease']);
export type MovementDirection = z.infer<typeof movementDirectionSchema>;

/**
 * The default reason of each direction, which a row left without one takes (R4),
 * and the direction a new movement starts from before its level is typed (caiet
 * UC50 step 2; docs/shape-ops-default-reasons.md §Decision 3). The operator sets
 * them in "Motive implicite", and the last reason used in a direction becomes its
 * default. Absent: not set yet.
 */
export const defaultReasonsSchema = z.object({
  increase: stockMovementReasonSchema.optional(),
  decrease: stockMovementReasonSchema.optional(),
  direction: movementDirectionSchema.optional(),
});
export type DefaultReasons = z.infer<typeof defaultReasonsSchema>;

/**
 * Lot number length: 1–40 characters. Owner decision, 2026-09-14 (research.md
 * R13): `core.stock.lot` stays varchar(40), because widening it means dropping
 * and rebuilding the `core.stock_effective` view and two continuous aggregates
 * that depend on the column. The wireframe's 50 is overruled by that decision.
 * One constant, so the check and the column cannot disagree: a longer lot would
 * pass validation and then fail at confirm with a database error.
 */
export const LOT_MAX_LENGTH = 40;

/**
 * Comment length: the width of `core.stock.comment`, varchar(500). Checked per
 * row on both reporting paths (COMMENT_TOO_LONG), so a longer comment is refused
 * where it was entered instead of failing the ledger INSERT at confirm
 * (docs/shape-ops-row-validation.md §Decision 1).
 */
export const COMMENT_MAX_LENGTH = 500;

export const stockRowSchema = z.object({
  stockDate: z.string().date().optional(),
  siteCode: z.string().min(1).max(64).optional(),
  medicineCode: z.string().min(1).max(64),
  lot: z.string().min(1).max(LOT_MAX_LENGTH).describe('1–40 characters'),
  lotExpiry: z
    .string()
    .regex(/^\d{4}-\d{2}(-\d{2})?$/, 'YYYY-MM or YYYY-MM-DD')
    .describe('Caiet UC40: YYYY-MM = end-of-month'),
  currentQuantity: z.number().int().nonnegative(),
  reason: stockMovementReasonSchema,
  comment: z.string().max(COMMENT_MAX_LENGTH).optional(),
  correctionOf: z.string().uuid().optional(),
});
export type StockRow = z.infer<typeof stockRowSchema>;

export const stockSubmissionRequestSchema = z.object({
  correlationId: z.string().uuid().optional(),
  stockDate: z.string().date().optional(),
  siteCode: z.string().min(1).max(64).optional(),
  rows: z.array(stockRowSchema).min(1).max(2000),
});
export type StockSubmissionRequest = z.infer<typeof stockSubmissionRequestSchema>;

export const stockSubmissionResponseSchema = z.object({
  submissionId: z.string().uuid(),
  acceptedRows: z.number().int().nonnegative(),
  receivedAt: z.string().datetime(),
});
export type StockSubmissionResponse = z.infer<typeof stockSubmissionResponseSchema>;

// UC50 draft ─────────────────────────────────────────────────────────────────

export const lotMatchSchema = z.enum(['NEW', 'EXISTING', 'UNKNOWN']);
export type LotMatch = z.infer<typeof lotMatchSchema>;

export const draftRowValidationCodeSchema = z.enum([
  'MEDICINE_REQUIRED',
  'MEDICINE_CODE_INACTIVE',
  // UC40-08: the code is in no nomenclature at all, on a new lot
  // (docs/shape-ops-row-validation.md §Decision 3).
  'MEDICINE_UNKNOWN',
  'LOT_REQUIRED',
  'LOT_TOO_LONG',
  'REASON_REQUIRED',
  'REASON_UNKNOWN',
  // UC50-15, CF-15: a reason the Registrar's type does not allow, on both paths.
  'REASON_NOT_ALLOWED',
  'REASON_DIRECTION',
  'EXPIRY_REQUIRED',
  'EXPIRY_FORMAT',
  // UC50-04, CF-02: the row names a lot the caller reaches with another expiry
  // than the lot's (docs/shape-ops-existing-lot-alerts.md §Decision 2). Manual
  // path only; the value entered is kept.
  'EXPIRY_MISMATCH',
  'QUANTITY_REQUIRED',
  'QUANTITY_NON_INTEGER',
  'QUANTITY_NEGATIVE',
  'QUANTITY_UNCHANGED',
  'QUANTITY_OUT_OF_RANGE',
  'COMMENT_REQUIRED',
  'COMMENT_TOO_LONG',
  'DUPLICATE_ROW',
  'CORRECTION_WINDOW_EXPIRED',
  'CORRECTION_TARGET_MISSING',
  'CORRECTION_MISMATCH',
  // UC50-16, CF-57: a manual row has no stock date; confirm dates it.
  'STOCK_DATE_NOT_ALLOWED',
  // specs/054 R11: core folds a row's source issues into its validation errors.
  // An import code that is a row code above keeps it; NEGATIVE_QUANTITY is the
  // import's own name for a negative cell; any other import code is reported as
  // SOURCE_VALUE_INVALID, while the row's `sourceIssues` keep the file's code.
  'NEGATIVE_QUANTITY',
  'SOURCE_VALUE_INVALID',
]);
export type DraftRowValidationCode = z.infer<typeof draftRowValidationCodeSchema>;

export const draftRowValidationErrorSchema = z.object({
  field: z.string(),
  code: draftRowValidationCodeSchema,
  // Rendered from the error catalogue (error-catalogue.ts), Romanian where the
  // caller asked for no language; `params` are the values the sentence names, so
  // an edge can render it again in the language the client asked for.
  message: z.string(),
  params: z.record(z.string(), z.string()).optional(),
});
export type DraftRowValidationError = z.infer<typeof draftRowValidationErrorSchema>;

/**
 * An invalid cell kept from an import, with the text the file held (R11). The
 * code is the import's own (e.g. NEGATIVE_QUANTITY, EXPIRY_FORMAT), so it is not
 * limited to the row codes above. Editing `field` clears the issue.
 */
export const draftRowSourceIssueSchema = z.object({
  field: z.string().min(1),
  code: z.string().min(1),
  raw: z.string(),
});
export type DraftRowSourceIssue = z.infer<typeof draftRowSourceIssueSchema>;

/** Draft row — extends StockRow with editor metadata + per-row validation. */
export const stockDraftRowSchema = stockRowSchema
  .partial({
    medicineCode: true,
    lot: true,
    lotExpiry: true,
    currentQuantity: true,
    reason: true,
  })
  .extend({
    clientRowId: z.string().min(1, 'clientRowId required'),
    validationErrors: z.array(draftRowValidationErrorSchema).default([]),
    lotMatch: lotMatchSchema.default('UNKNOWN'),
    // Server-computed, ignored on input (R1, R2): the stock at the draft's
    // location for (medicine, lot) before this row, counting earlier rows of the
    // same draft with the same key. The difference shown is
    // currentQuantity − previousQuantity.
    //
    // Not `.nonnegative()`, although a real stock never is: the value is the
    // ledger's SUM, and a ledger can net below zero (an outflow reported before
    // its inflow, a correction of an old movement). It is kept as it is,
    // never clamped to 0, because confirm writes currentQuantity − previousQuantity
    // and only the real value leaves SUM(quantity_signed) equal to the level the
    // operator entered (R1). A clamped display would also show a difference of 0
    // for a level of 0 that is in fact a movement.
    previousQuantity: z.number().int().optional(),
    // Server-computed (R2): the latest expiry of this lot at the locations the
    // caller reaches (CF-43: the Registrar Administrator's whole Registrar, an
    // Operator's associated locations), or null when none of them has reported
    // the lot (docs/shape-ops-existing-lot-alerts.md §Decision 1).
    knownExpiry: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD')
      .nullable()
      .optional(),
    // Server-computed with knownExpiry (BUG-1-029): how that lot's expiry was
    // reported, which decides how knownExpiry is shown. Null with it.
    knownExpiryPrecision: z.enum(['DAY', 'MONTH']).nullable().optional(),
    // Persisted (R11). Optional so a row written before spec 054 still reads.
    sourceIssues: z.array(draftRowSourceIssueSchema).optional(),
  });
export type StockDraftRow = z.infer<typeof stockDraftRowSchema>;

export const stockDraftStateSchema = z.object({
  id: z.string().uuid(),
  operatorIdnp: z.string().length(13),
  // The author's display name, written once at creation (R7). Null on drafts
  // started before spec 054.
  operatorName: z.string().nullable().optional(),
  registrarId: z.string().uuid(),
  siteId: z.string().uuid().nullable(),
  rows: z.array(stockDraftRowSchema),
  defaultReasons: defaultReasonsSchema.nullable(),
  version: z.number().int().nonnegative(),
  createdAtIso: z.string().datetime(),
  updatedAtIso: z.string().datetime(),
});
export type StockDraftState = z.infer<typeof stockDraftStateSchema>;

export const createDraftRequestSchema = z.object({
  siteId: z.string().uuid().nullable().optional(),
});
export type CreateDraftRequest = z.infer<typeof createDraftRequestSchema>;

export const upsertDraftRequestSchema = z.object({
  expectedVersion: z.number().int().nonnegative(),
  rows: z.array(stockDraftRowSchema).max(2000),
  // Absent keeps the stored value (constitution art. III); null clears it.
  defaultReasons: defaultReasonsSchema.nullable().optional(),
  siteId: z.string().uuid().nullable().optional(),
});
export type UpsertDraftRequest = z.infer<typeof upsertDraftRequestSchema>;

export const confirmDraftRequestSchema = z.object({
  expectedVersion: z.number().int().nonnegative(),
});
export type ConfirmDraftRequest = z.infer<typeof confirmDraftRequestSchema>;

export const draftConflictErrorSchema = z.object({
  code: z.literal('VERSION_CONFLICT'),
  currentVersion: z.number().int().nonnegative(),
});
export type DraftConflictError = z.infer<typeof draftConflictErrorSchema>;
